import { useMemo } from "react";
import DOMPurify from "dompurify";
import "quill/dist/quill.core.css";

/**
 * Renders Quill-authored HTML read-only.
 *
 * Deliberately does NOT instantiate Quill. A Quill instance builds an Editor,
 * which builds a Delta, which drags `quill-delta`'s ESM/CJS interop into the
 * bundle — the source of "TypeError: h is not a constructor" in production.
 * Reusing Quill's `.ql-container` / `.ql-editor` classes gives identical
 * output with none of that.
 */
const QuillEditorViewOnly = ({ value, className = "text-xs" }) => {
  const html = useMemo(() => {
    if (typeof value !== "string" || !value.trim()) return "";
    return DOMPurify.sanitize(value.trim(), { USE_PROFILES: { html: true } });
  }, [value]);

  return (
    <div className={`quill-view-only ${className}`}>
      <style>
        {`
          /* Inherit from the wrapper so a Tailwind text-* class drives the size. */
          .quill-view-only .ql-container {
            border: none !important;
            box-shadow: none !important;
            font-family: inherit;
            font-size: inherit;
          }
          .quill-view-only .ql-editor {
            padding: 0;
            margin: 0;
            height: auto;
            overflow: visible;
            line-height: 1.5;
          }
          /* Quill leaves headings to browser defaults (h1 = 2em) — rein them in. */
          .quill-view-only .ql-editor h1 { font-size: 1.5em; font-weight: 600; }
          .quill-view-only .ql-editor h2 { font-size: 1.3em; font-weight: 600; }
          .quill-view-only .ql-editor h3 { font-size: 1.15em; font-weight: 600; }
          .quill-view-only .ql-editor h4,
          .quill-view-only .ql-editor h5,
          .quill-view-only .ql-editor h6 { font-size: 1em; font-weight: 600; }
          /* Toolbar size formats, scaled down from 0.75 / 1.5 / 2.5em. */
          .quill-view-only .ql-editor .ql-size-small { font-size: 0.85em; }
          .quill-view-only .ql-editor .ql-size-large { font-size: 1.2em; }
          .quill-view-only .ql-editor .ql-size-huge  { font-size: 1.5em; }
        `}
      </style>
      <div className="ql-container">
        <div
          className="ql-editor font-medium text-charcoal"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </div>
  );
};

export default QuillEditorViewOnly;
